import { Badge, Code, Group, Stack, Text, Tooltip } from '@mantine/core'
import { cloneElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { MilestoneCamera, PipelineSource } from '@/types/vlm'
import { formatPatrolSchedule } from './pipeline-create/patrol/patrolSchedule'
import { PIPELINE_TYPE_CONFIG, buildHttpPushUrl } from './vlmPipelineConfig'

type PipelineInputSourceProps = {
  source: PipelineSource
}

export const PipelineInputSource = ({ source }: PipelineInputSourceProps) => {
  const intl = useIntl()
  const { icon } = PIPELINE_TYPE_CONFIG[source.type]

  return (
    <Group gap={8} wrap="nowrap" align="center">
      {cloneElement(icon, { size: 16 })}

      {source.type === 'directory' && <Code fz={12}>{source.watch_path}</Code>}

      {source.type === 'http_push' && (
        <Code fz={12}>
          {buildHttpPushUrl(source.port, source.endpoint_path)}
        </Code>
      )}

      {source.type === 'visionplatform' && (
        <Text size="xs">{source.applications[0]?.name ?? '—'}</Text>
      )}

      {source.type === 'milestone' && (
        <MilestoneSourceSummary
          label={source.milestone_server?.name}
          cameras={source.cameras}
          detail={source.milestone_server?.server_url}
        />
      )}

      {source.type === 'virtual_patrol' && (
        <MilestoneSourceSummary
          label={
            source.video_source === 'rtsp'
              ? intl.formatMessage({
                  id: 'vlm.pipelines.create.patrol.videoSource.rtsp'
                })
              : source.milestone_server?.name
          }
          cameras={source.cameras}
          detail={formatPatrolSchedule(source.schedule, intl)}
        />
      )}
    </Group>
  )
}

type MilestoneSourceSummaryProps = {
  label?: string
  cameras: MilestoneCamera[]
  detail?: string
}

const MilestoneSourceSummary = ({
  label,
  cameras,
  detail
}: MilestoneSourceSummaryProps) => (
  <Stack gap={0} miw={0}>
    <Group gap={6} wrap="nowrap">
      <Text size="xs" lineClamp={1}>
        {label ?? '—'}
      </Text>

      <Tooltip
        label={cameras.map((camera) => camera.name).join(', ')}
        disabled={cameras.length === 0}
        openDelay={300}
        maw={320}
        multiline
        withArrow
      >
        <Badge
          size="xs"
          variant="light"
          color="gray"
          tt="none"
          style={{ flexShrink: 0 }}
        >
          <FormattedMessage
            id="vlm.pipelines.list.cameraCount"
            values={{ count: cameras.length }}
          />
        </Badge>
      </Tooltip>
    </Group>

    {detail && (
      <Text size="xs" c="dimmed" lineClamp={1}>
        {detail}
      </Text>
    )}
  </Stack>
)
